export const symbols = ['', 'k', 'm', 'b'];
// Scale factors for each symbol
const SCALES = {
  '': 1,
  k: 1000,
  m: 1000000,
  b: 1000000000
}; // Minimum value thresholds (inclusive) before showing non-zero values
// Values less than or equal to these will be displayed as "0{symbol}"

const MIN_THRESHOLDS = {
  '': 0,
  k: 4,
  // Values ≤ 4 will show as 0k
  m: 4999,
  // Values ≤ 4,999 will show as 0m
  b: 4999999 // Values ≤ 4,999,999 will show as 0b

};
// Abbreviates a number according to the specified symbol (k, m, b)
export default function abbreviateNumber(num, options) {
  const {
    symbol,
    autoRoundingDigit = 2,
    fixedDigit,
    omitNegativeSymbol = false
  } = options || {}; // Handle negatives

  const sign = Math.sign(num) >= 0;
  num = Math.abs(num); // If symbol is explicitly specified

  if (symbol) {
    if (num <= MIN_THRESHOLDS[symbol]) {
      const result = formatNumber(0, autoRoundingDigit, fixedDigit);
      return (!sign && !omitNegativeSymbol ? '-' : '') + result + symbol;
    }

    const scale = SCALES[symbol];
    const scaled = num / scale;
    const result = formatNumber(scaled, autoRoundingDigit, fixedDigit);
    return (!sign && !omitNegativeSymbol ? '-' : '') + result + symbol;
  } // Auto-detect tier when no symbol is specified


  const LOG_BASE = 3; // log10 scaling factor for thousands

  const tier = Math.floor(Math.log10(Math.max(1, num)) / LOG_BASE); // If tier is 0, no suffix needed

  if (tier === 0) return (!sign && !omitNegativeSymbol ? '-' : '') + num.toString(); // Get suffix and determine scale

  let suffix = symbols[tier];
  const scale = Math.pow(10, tier * LOG_BASE);
  const scaled = num / scale;
  let rounded = formatNumber(scaled, autoRoundingDigit, fixedDigit); // Edge case: If rounding results in "1000" (e.g., 999.995k becomes 1000k),
  // adjust to the next tier (e.g., "1m").

  if (rounded === '1000' && tier < symbols.length - 1) {
    rounded = '1';
    suffix = symbols[tier + 1];
  }

  if (!suffix) throw new RangeError();
  return (!sign && !omitNegativeSymbol ? '-' : '') + rounded + suffix;
} // Helper function to format numbers with proper digits and padding

function formatNumber(value, autoRoundingDigit, fixedDigit) {
  const isFixed = typeof fixedDigit === 'number';
  const factor = Math.pow(10, isFixed ? fixedDigit : autoRoundingDigit);
  const num = Math.round(value * factor + 1e-9) / factor;
  let result = isFixed ? num.toFixed(fixedDigit) : num.toFixed(autoRoundingDigit);
  if (isFixed) return result; // Edge case: If the rounded number is exactly 1000 (e.g., 999.995 rounded to 2 digits)
  // and we are not formatting to 0 digits, we use a different format
  // to prevent `toFixed` from displaying "1000.00"

  if (num === 1000 && autoRoundingDigit !== 0) {
    result = `${Math.trunc(value * 100) / 100}`;
  } // If the number has exactly ".00" remove the decimal part entirely.
  // Example: "12.00" -> "12" (but "12.0" stays "12.0" if digit is 1)


  if (autoRoundingDigit !== 1) {
    result = result.replace(/(\d+)\.0+(?=\D*$|\s*$)/, '$1');
  } // Keep zeros if auto round digit is greater than or equal to 3


  if (autoRoundingDigit >= 3 || autoRoundingDigit === 1) {
    return result;
  } // Remove trailing zeros


  return result.replace(/(\d+\.\d*?)0+(?=\D*$|\s*$)/, '$1');
}